import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Apollo } from 'apollo-angular';
import { cloneDeep } from 'lodash-es';
import { forkJoin, from, Observable, shareReplay, switchMap } from 'rxjs';
import { PDFME_CONFIG } from '../pdfme.config';
import type { Font, GeneratorOptions, Lang, Plugin, Template } from '@pdfme/common';

@Injectable({ providedIn: 'root' })
export class PdfmeService {
  protected readonly apollo = inject(Apollo);
  private readonly translocoService = inject(TranslocoService);
  private readonly config = inject(PDFME_CONFIG);

  private fonts$: Observable<Font> | undefined = undefined;
  private plugins$: Observable<Record<string, Plugin<any>>> | undefined = undefined;

  getLang(): Lang {
    const lang = this.translocoService.getActiveLang().split('-')[0] as Lang;
    if (this.config.supportedLangs.includes(lang)) {
      return lang;
    } else {
      return this.config.fallbackLang;
    }
  }

  getFonts(): Observable<Font> {
    if (!this.fonts$) {
      this.fonts$ = this.config.fonts().pipe(shareReplay(1));
    }
    return this.fonts$;
  }

  getPlugins(): Observable<Record<string, Plugin<any>>> {
    if (!this.plugins$) {
      this.plugins$ = this.config.plugins().pipe(shareReplay(1));
    }
    return this.plugins$;
  }

  generate(
    template: Template,
    inputs: Record<string, unknown>[],
    options?: GeneratorOptions
  ): Observable<Uint8Array> {
    return forkJoin({
      font: this.getFonts(),
      plugins: this.getPlugins(),
      generator: from(import('@pdfme/generator'))
    }).pipe(
      switchMap(({ font, plugins, generator }) =>
        from(generator.generate({ template, inputs, plugins, options: { ...options, font } }))
      )
    );
  }

  getDefaultFieldSize(fieldType?: string): { width: number; height: number } {
    switch (fieldType) {
      case 'image':
        return { width: 50, height: 50 };
      case 'table':
        return { width: 150, height: 30 };
      case 'qrcode':
        return { width: 30, height: 30 };
      case 'signature':
        return { width: 60, height: 25 };
      default:
        return { width: 80, height: 10 };
    }
  }

  addField(
    template: Template,
    name: string,
    content?: string,
    type: string = 'text',
    pageIndex: number = 0
  ): Template {
    const newItem: any = {
      readOnly: true,
      name: `${name}_${Date.now()}`,
      content: content ? `{${content}}` : '',
      type,
      position: { x: 30, y: 30 },
      ...this.getDefaultFieldSize(type)
    };

    const updated = cloneDeep(template) as any;
    const idx = Math.min(pageIndex, updated.schemas.length - 1);
    updated.schemas[idx] = [...(updated.schemas[idx] ?? []), newItem];
    return updated;
  }
}
